import { useCallback, useState } from 'react';

type AsyncFunction<T = any> = (...args: any[]) => Promise<T>;
type SyncFunction = (...args: any[]) => void;

interface UseAsyncButtonResult<T = any> {
  isLoading: boolean;
  handleClick: (...args: any[]) => Promise<T | void>;
  error: Error | null;
}

const isPromise = <T>(value: unknown): value is Promise<T> => typeof (value as Promise<T> | undefined)?.then === 'function';

export const useAsyncButton = <T = any>(
  handler?: AsyncFunction<T> | SyncFunction,
  options: {
    onError?: (error: Error) => void;
    onSuccess?: (result: T) => void;
  } = {},
): UseAsyncButtonResult<T> => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handleClick = useCallback(
    async (...args: any[]) => {
      if (!handler || isLoading) return;

      const succeed = (result: T | void) => {
        if (result as T) {
          options.onSuccess?.(result as T);
        }
        return result;
      };

      const fail = (err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        if (!options.onError) {
          throw error;
        }
        options.onError(error);
      };

      setError(null);

      let result: T | Promise<T> | void;
      try {
        result = handler(...args);
      } catch (err) {
        return fail(err);
      }

      // Синхронный обработчик в loading не переводим. Иначе кнопка перерисовывалась бы прямо
      // посреди обработки клика (иконка заменяется спиннером) и элемент, по которому кликнули,
      // отрывался бы от DOM — а логика «клик снаружи» (useCloseOnOutsideOrEsc и подобная)
      // читает DOM уже после этого и принимает такой клик за внешний.
      if (!isPromise<T>(result)) {
        return succeed(result);
      }

      try {
        setIsLoading(true);
        return succeed(await result);
      } catch (err) {
        return fail(err);
      } finally {
        setIsLoading(false);
      }
    },
    [handler, isLoading, options],
  );

  return {
    isLoading,
    handleClick,
    error,
  };
};
